import "@/App.css";
import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AnalyticsProvider } from "./contexts/AnalyticsContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { Layout } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import LanguageSelector from "./components/LanguageSelector";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import SymptomCheck from "./pages/SymptomCheck";
import DiagnosisResult from "./pages/DiagnosisResult";
import History from "./pages/History";
import SharedDiagnosis from "./pages/SharedDiagnosis";
import Subscription from "./pages/Subscription";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Consultations from "./pages/Consultations";
import NearbyFacilities from "./pages/NearbyFacilities";
import HealthLibrary from "./pages/HealthLibrary";
import AuthCallback from "./pages/AuthCallback";
import Referrals from "./pages/Referrals";
import Prescriptions from "./pages/Prescriptions";
import HealthDiary from "./pages/HealthDiary";
import GoogleCallback from "./pages/auth/GoogleCallback";
import OwnerPanel from "./pages/OwnerPanel";
import MediProviderRegister from "./pages/MediProviderRegister";
import MediProviderLogin from "./pages/MediProviderLogin";
import MediProviderDashboard from "./pages/MediProviderDashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CopyrightPolicy from "./pages/CopyrightPolicy";
import DeleteAccount from "./pages/DeleteAccount";
import DataDeletion from "./pages/DataDeletion";

// Admin Pages
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminProviders from "./pages/AdminProviders";
import AdminUsers from "./pages/AdminUsers";
import AdminNotifications from "./pages/AdminNotifications";
import AdminLayout from "./components/AdminLayout";

// Register service worker for PWA
const registerServiceWorker = () => {
    if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
            navigator.serviceWorker.register('/service-worker.js')
                .then(registration => console.log('SW registered'))
                .catch(err => console.log('SW registration failed'));
        });
    }
};

const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    return children;
};

const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
    if (isAuthenticated) return <Navigate to="/dashboard" replace />;
    return children;
};

// Language selection wrapper - shows on first visit
const LanguageSelectionWrapper = ({ children }) => {
    const { language, setLanguage, hasSelectedLanguage, confirmLanguageSelection } = useLanguage();
    const location = useLocation();
    
    // Skip language selector for legal pages (Privacy, Terms) - required for Google Play compliance
    const legalPages = ['/privacy', '/terms', '/copyright', '/delete-account', '/data-deletion'];
    const isLegalPage = legalPages.includes(location.pathname);
    
    // Show language selector on first visit (except for legal pages)
    if (!hasSelectedLanguage && !isLegalPage) {
        return (
            <LanguageSelector
                selectedLanguage={language}
                onSelectLanguage={setLanguage}
                onContinue={confirmLanguageSelection}
            />
        );
    }
    
    return children;
};

const AppRoutes = () => {
    const location = useLocation();
    
    useEffect(() => { registerServiceWorker(); }, []);

    // CRITICAL: Check for session_id SYNCHRONOUSLY during render to handle Google OAuth callback
    // This prevents race conditions by processing session_id BEFORE ProtectedRoute checks auth
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }
    
    return (
        <LanguageSelectionWrapper>
            <Routes>
                <Route path="/" element={<PublicRoute><Layout><Landing /></Layout></PublicRoute>} />
                <Route path="/login" element={<PublicRoute><Layout><Login /></Layout></PublicRoute>} />
                <Route path="/register" element={<PublicRoute><Layout><Register /></Layout></PublicRoute>} />
                <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
                <Route path="/reset-password/:token" element={<Layout><ResetPassword /></Layout>} />
                
                <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
                <Route path="/symptom-check" element={<ProtectedRoute><Layout><SymptomCheck /></Layout></ProtectedRoute>} />
                <Route path="/diagnosis/:id" element={<ProtectedRoute><Layout><DiagnosisResult /></Layout></ProtectedRoute>} />
                <Route path="/history" element={<ProtectedRoute><Layout><History /></Layout></ProtectedRoute>} />
                <Route path="/subscription" element={<ProtectedRoute><Layout><Subscription /></Layout></ProtectedRoute>} />
                <Route path="/subscription/success" element={<ProtectedRoute><Layout><Subscription /></Layout></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
                <Route path="/consultations" element={<ProtectedRoute><Layout><Consultations /></Layout></ProtectedRoute>} />
                <Route path="/referrals" element={<ProtectedRoute><Layout><Referrals /></Layout></ProtectedRoute>} />
                <Route path="/prescriptions" element={<ProtectedRoute><Layout><Prescriptions /></Layout></ProtectedRoute>} />
                <Route path="/health-diary" element={<ProtectedRoute><Layout><HealthDiary /></Layout></ProtectedRoute>} />
                <Route path="/nearby" element={<ProtectedRoute><Layout><NearbyFacilities /></Layout></ProtectedRoute>} />
                <Route path="/health-library" element={<ProtectedRoute><Layout><HealthLibrary /></Layout></ProtectedRoute>} />
                
                <Route path="/shared/:token" element={<SharedDiagnosis />} />
                <Route path="/auth/google/callback" element={<GoogleCallback />} />
                <Route path="/owner" element={<ProtectedRoute><OwnerPanel /></ProtectedRoute>} />
                <Route path="/mediprovider/register" element={<Layout><MediProviderRegister /></Layout>} />
                <Route path="/mediprovider/login" element={<MediProviderLogin />} />
                <Route path="/mediprovider/dashboard" element={<MediProviderDashboard />} />
                
                {/* Legal Pages - Public */}
                <Route path="/privacy" element={<Layout><PrivacyPolicy /></Layout>} />
                <Route path="/terms" element={<Layout><TermsOfService /></Layout>} />
                <Route path="/copyright" element={<Layout><CopyrightPolicy /></Layout>} />
                <Route path="/delete-account" element={<DeleteAccount />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
                <Route path="/admin/providers" element={<AdminLayout><AdminProviders /></AdminLayout>} />
                <Route path="/admin/users" element={<AdminLayout><AdminUsers /></AdminLayout>} />
                <Route path="/admin/notifications" element={<AdminLayout><AdminNotifications /></AdminLayout>} />
                
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </LanguageSelectionWrapper>
    );
};

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <LanguageProvider>
                    <AuthProvider>
                        <AnalyticsProvider>
                            <AppRoutes />
                            <Toaster position="top-right" />
                        </AnalyticsProvider>
                    </AuthProvider>
                </LanguageProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
